import {
  Bars3Icon,
  CheckBadgeIcon,
  LockClosedIcon,
  PaperAirplaneIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { FC, useState } from "react";
import { OfficeButton, OfficeHeader, Show, Tabs } from "components";
import { useAppLocation, useCurrentUser } from "_graphql/cache/auth";

import AcceptAgreementContainer from "./accept-terms-of-agreement";
import AddParticipantsContainer from "./add-participants";
import CloseOfferContainer from "./close-offer";
import { DocumentCheckIcon } from "@heroicons/react/24/solid";
import FleetPicker from "./components/fleet-picker";
import OfferAssociates from "./associates";
import OfferOverView from "./overview";
import OfferParticipation from "./participation";
import PublishUnpublishOfferContainer from "./components/publish-unpublish-offer";
import { PublishedStatus } from "_graphql/__generated__/globalTypes";
import SendEmailsContainer from "./send-emails";
import { useOffer } from "hooks/data/use-offers";
import { useUrlState } from "utils";

type Props = {};

const tabs = [
  { name: "Offer Participants", href: "participants" },
  { name: "Participant's Associates", href: "associates" },
];

const OfferPage: FC<Props> = () => {
  const currentUser = useCurrentUser();
  const [modal, setModal] = useUrlState("modal");
  const [orderStatus] = useUrlState("orderStatus");
  const { params } = useAppLocation();
  const offerId = params?.offer || "";

  const { offer, refetch } = useOffer({
    offer_id: offerId,
  });

  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);

  const isOfferPublished =
    offer?.offer_published?.published_status === PublishedStatus.PUBLISHED;

  return (
    <main className="flex-1 flex flex-col overflow-hidden bg-background-500">
      <OfficeHeader
        renderActions={() => (
          <>
            {/* Desktop / Tablet actions */}
            <div className="hidden sm:flex sm:items-center sm:space-x-2">
              <FleetPicker offer={offer} />
              <Show
                if={Boolean(currentUser?.employee?.accept_waiver_aggreement)}
              >
                <OfficeButton
                  variant={isOfferPublished ? "danger" : "primary"}
                  size="sm"
                  onClick={() => setModal("publish")}
                >
                  {isOfferPublished ? (
                    <XMarkIcon className="w-5 h-5 mr-2" />
                  ) : (
                    <CheckBadgeIcon className="w-5 h-5 mr-2" />
                  )}
                  <span className="inline text-xs sm:hidden">
                    {isOfferPublished ? "Unpub." : "Publish"}
                  </span>
                  <span className="hidden sm:inline">
                    {isOfferPublished ? "Unpublish" : "Publish"} Offer
                  </span>
                </OfficeButton>
              </Show>
              <Show
                if={!Boolean(currentUser?.employee?.accept_waiver_aggreement)}
              >
                <OfficeButton size="sm" onClick={() => setModal("agreement")}>
                  <DocumentCheckIcon className="w-5 h-5 mr-2" />
                  <span className="inline text-xs sm:hidden">Agree</span>
                  <span className="hidden sm:inline">Agreement</span>
                </OfficeButton>
              </Show>
              <OfficeButton size="sm" onClick={() => setModal("send")}>
                <PaperAirplaneIcon className="w-5 h-5 mr-2" />
                <span className="inline text-xs sm:hidden">Send</span>
                <span className="hidden sm:inline">Send Emails</span>
              </OfficeButton>
              <OfficeButton size="sm" onClick={() => setModal("close")}>
                <LockClosedIcon className="w-5 h-5 mr-2" />
                <span className="inline text-xs sm:hidden">Close</span>
                <span className="hidden sm:inline">Close Offer</span>
              </OfficeButton>
            </div>

            {/* Mobile actions: single compact menu button */}
            <div className="sm:hidden flex items-center">
              <button
                aria-label="Open actions"
                onClick={() => setMobileActionsOpen((s) => !s)}
                className="p-2 rounded-md hover:bg-background-400 focus:outline-none focus:ring-2 focus:ring-offset-1"
              >
                <Bars3Icon className="w-6 h-6" />
              </button>
            </div>
          </>
        )}
      />

      {/* Mobile action sheet — appears from bottom on small screens */}
      {mobileActionsOpen && (
        <div
          className="sm:hidden fixed inset-x-0 bottom-0 z-50 bg-white dark:bg-background-600 border-t shadow-lg"
          aria-hidden={!mobileActionsOpen}
        >
          <div className="max-w-xl mx-auto py-3 px-4 space-y-2">
            <div className="flex items-center justify-between">
              <div className="font-medium">Actions</div>
              <button
                aria-label="Close actions"
                onClick={() => setMobileActionsOpen(false)}
                className="p-2 rounded-md"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => {
                  setMobileActionsOpen(false);
                  setModal("send");
                }}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-primary-600 text-white"
              >
                <PaperAirplaneIcon className="w-5 h-5" />
                <span className="text-sm">Send Emails</span>
              </button>

              <button
                onClick={() => {
                  setMobileActionsOpen(false);
                  setModal("close");
                }}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-slate-100 dark:bg-background-500"
              >
                <LockClosedIcon className="w-5 h-5" />
                <span className="text-sm">Close Offer</span>
              </button>

              <button
                onClick={() => {
                  setMobileActionsOpen(false);
                  setModal("publish");
                }}
                className={`w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md ${
                  isOfferPublished
                    ? "bg-red-600 text-white"
                    : "bg-emerald-600 text-white"
                }`}
              >
                {isOfferPublished ? (
                  <XMarkIcon className="w-5 h-5" />
                ) : (
                  <CheckBadgeIcon className="w-5 h-5" />
                )}
                <span className="text-sm">
                  {isOfferPublished ? "Unpublish" : "Publish"}
                </span>
              </button>

              <Show
                if={!Boolean(currentUser?.employee?.accept_waiver_aggreement)}
              >
                <button
                  onClick={() => {
                    setMobileActionsOpen(false);
                    setModal("agreement");
                  }}
                  className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-slate-100 dark:bg-background-500"
                >
                  <DocumentCheckIcon className="w-5 h-5" />
                  <span className="text-sm">Agreement</span>
                </button>
              </Show>

              <button
                onClick={() => {
                  setMobileActionsOpen(false);
                  setModal("create");
                }}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-blue-600 text-white col-span-2"
              >
                <span className="text-sm">Add Participant</span>
              </button>

              <div className="col-span-2">
                <FleetPicker offer={offer} />
              </div>
            </div>
          </div>
        </div>
      )}

      <Tabs tabs={tabs} orderStatus={orderStatus} defaultTab="Overview" />
      <div className="flex flex-1 overflow-y-auto">
        <div className="flex-1 mx-auto min-w-0 sm:py-6 py-4 px-4 sm:px-6 overflow-hidden overflow-y-auto light flex">
          {!orderStatus && <OfferOverView />}
          {orderStatus === "participants" && (
            <OfferParticipation isOfferPublished={isOfferPublished} />
          )}
          {orderStatus === "associates" && <OfferAssociates />}
        </div>
      </div>
      <AddParticipantsContainer
        open={modal === "create"}
        setOpen={(val: boolean) => setModal(val ? "create" : undefined)}
      />
      <SendEmailsContainer
        open={modal === "send"}
        setOpen={(val: boolean) => setModal(val ? "send" : undefined)}
      />
      <CloseOfferContainer
        open={modal === "close"}
        setOpen={(val: boolean) => setModal(val ? "close" : undefined)}
      />
      <AcceptAgreementContainer
        open={modal === "agreement"}
        setOpen={(val: boolean) => setModal(val ? "agreement" : undefined)}
      />
      <PublishUnpublishOfferContainer
        open={modal === "publish"}
        setOpen={(val: boolean) => {
          setModal(val ? "publish" : undefined);
        }}
        offerId={offerId}
        isPublished={isOfferPublished}
        refetch={refetch}
      />
    </main>
  );
};

export default OfferPage;
